import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
    useAssessmentsCatalogQuery,
    usePopQuizAttemptStartMutation,
    usePopQuizItemSearchQuery,
} from 'src/generated/graphql';
import {POP_QUIZ_SETTINGS, usePopQuizSettings} from 'src/lib/pop-quiz/pop-quiz-settings';

import {
    isDebugQuizSuppressed,
    LONG_SNOOZE_DAYS,
    mapRelatedLectures,
    MIN_QUIZ_QUESTIONS,
    Phase,
    PopQuizContinueCourseSelected,
    PopQuizDismissed,
    PopQuizFeedbackRating,
    PopQuizFeedbackSubmitted,
    PopQuizItem,
    PopQuizModalProps,
    PopQuizPresented,
    PopQuizRecommendedAssessmentLinkSelected,
    PopQuizRecommendedAssessmentPresented,
    PopQuizSnoozed,
    publishPopQuizEvent,
    QuizCandidate,
    QuizStarted,
    selectPrimaryDomainId,
    selectRecommendedAssessment,
} from './pop-quiz';
import {useQuizOrchestrator} from './pop-quiz/orchestrator/use-quiz-orchestrator';
import {PopQuizPromptPhase} from './pop-quiz/pop-quiz-prompt-phase.react-component';
import {PopQuizQuizPhase} from './pop-quiz/pop-quiz-quiz-phase.react-component';
import {PopQuizResultsPhase} from './pop-quiz/pop-quiz-results-phase.react-component';
import {PopQuizSnoozedPhase} from './pop-quiz/pop-quiz-snoozed-phase.react-component';

// --- Component ---

export function PopQuizModal({
    areOtherModalsOpen,
    enablePopQuizPP,
    popQuizV2ExperienceEnabled,
}: PopQuizModalProps): React.ReactElement | null {
    const {getPopQuizSetting, setPopQuizSetting} = usePopQuizSettings();

    const [isOpen, setIsOpen] = useState(false);
    const [phase, setPhase] = useState<Phase>('prompt');
    const [questions, setQuestions] = useState<PopQuizItem[]>([]);
    const [correctCount, setCorrectCount] = useState(0);
    const [attemptId, setAttemptId] = useState<string | null>(null);
    const [stopShowing, setStopShowing] = useState(false);

    // Allow ?showPopQuiz on the URL to bypass all cap/de-dupe checks (dev/QA only).
    // ?quizId= additionally forces a specific (synthetic) quiz for QA, bypassing orchestrator selection.
    const debugSearchParams =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const forcePopQuiz = !!debugSearchParams?.has('showPopQuiz');
    const forcedQuizId = debugSearchParams?.get('quizId') ?? null;

    // Orchestrator owns "which quiz (and why) should we show right now".
    const {quizData: orchestratorQuizData} = useQuizOrchestrator({
        enablePopQuizPP,
        popQuizV2ExperienceEnabled,
        getPopQuizSetting,
        forcePopQuiz,
    });

    // A forced ?quizId= always wins over the orchestrator's pick, and never touches real
    // enrollment data — it exists purely to let QA exercise the quiz/results flow on demand.
    const quizData: QuizCandidate | null = useMemo(() => {
        const forcedQuizIdNumber = forcedQuizId ? Number(forcedQuizId) : NaN;
        if (forcePopQuiz && Number.isFinite(forcedQuizIdNumber)) {
            return {
                quizId: forcedQuizIdNumber,
                quizTitle: 'Debug quiz',
                courseId: 0,
                courseTitle: 'Debug course',
                courseUrl: '/',
                courseProgress: 0,
                courseTopicId: null,
                isDebugQuiz: true,
                source: 'default',
            };
        }
        return orchestratorQuizData ? {...orchestratorQuizData, isDebugQuiz: forcePopQuiz} : null;
    }, [forcePopQuiz, forcedQuizId, orchestratorQuizData]);

    // Pre-fetch quiz items so they're ready when the prompt shows. relatedLectures is only
    // requested when popQuizV2ExperienceEnabled — control-group users don't pay for that payload.
    const {data: itemSearchData, isFetching: isItemSearchFetching} = usePopQuizItemSearchQuery(
        {
            quizId: String(quizData?.quizId ?? ''),
            includeRelatedLectures: popQuizV2ExperienceEnabled,
        },
        {enabled: !!quizData && enablePopQuizPP},
    );

    // Fetch the assessments catalog to find a related assessment for the course
    const {data: assessmentsCatalogData, isFetching: isAssessmentsCatalogFetching} =
        useAssessmentsCatalogQuery(
            {},
            {enabled: !!quizData && enablePopQuizPP && popQuizV2ExperienceEnabled},
        );
    const isAssessmentsCatalogSettled = !isAssessmentsCatalogFetching;

    const recommendedAssessment = useMemo(
        () =>
            popQuizV2ExperienceEnabled
                ? selectRecommendedAssessment(
                      (assessmentsCatalogData?.assessments ?? []).filter((a) => a != null),
                      quizData?.courseTopicId ?? null,
                  )
                : null,
        [popQuizV2ExperienceEnabled, assessmentsCatalogData, quizData],
    );

    // Map GQL items to the internal PopQuizItem shape, filtering to supported types.
    const supportedItems = useMemo((): PopQuizItem[] => {
        const rawItems = itemSearchData?.quizItemSearch?.items ?? [];
        return rawItems.reduce<PopQuizItem[]>((acc, item) => {
            if (item.__typename === 'MultipleChoiceQuizItem') {
                acc.push({
                    id: item.id,
                    text: item.text,
                    options: item.options,
                    isMultiSelect: false,
                    correctOptionIds: [item.correctOption.id],
                    explanation: item.explanation,
                    relatedLectures: mapRelatedLectures(item.relatedLectures),
                });
            } else if (item.__typename === 'MultiSelectQuizItem') {
                acc.push({
                    id: item.id,
                    text: item.text,
                    options: item.options,
                    isMultiSelect: true,
                    correctOptionIds: item.correctOptions.map((o) => o.id),
                    explanation: item.explanation,
                    relatedLectures: mapRelatedLectures(item.relatedLectures),
                });
            }
            return acc;
        }, []);
    }, [itemSearchData]);

    // Quiz attempt lifecycle mutations.
    // Calling these drives the server-side SimpleQuizAttempt* events that populate UDW learning days.
    const startAttempt = usePopQuizAttemptStartMutation();

    const hasShownRef = useRef(false);
    // Guards against duplicate snooze events if multiple dismiss paths fire before a re-render.
    const isSnoozingRef = useRef(false);

    // Show prompt once when quiz data is ready, items are loaded, the assessments catalog fetch
    // has settled and no other modals are open.
    useEffect(() => {
        if (
            quizData &&
            !areOtherModalsOpen &&
            !hasShownRef.current &&
            !isItemSearchFetching &&
            isAssessmentsCatalogSettled &&
            supportedItems.length >= MIN_QUIZ_QUESTIONS
        ) {
            hasShownRef.current = true;
            publishPopQuizEvent(
                new PopQuizPresented(
                    {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                    {id: quizData.courseId},
                ),
                quizData,
            );
            if (recommendedAssessment) {
                publishPopQuizEvent(
                    new PopQuizRecommendedAssessmentPresented(
                        {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                        {id: quizData.courseId},
                        {
                            id: Number(recommendedAssessment.id),
                            title: recommendedAssessment.title,
                            isBeta: recommendedAssessment.isBeta,
                            domainId: selectPrimaryDomainId(recommendedAssessment),
                        },
                    ),
                    quizData,
                );
            }
            setIsOpen(true);
        }
    }, [
        quizData,
        areOtherModalsOpen,
        isItemSearchFetching,
        isAssessmentsCatalogSettled,
        supportedItems,
        recommendedAssessment,
    ]);

    const recordShown = useCallback(() => {
        void setPopQuizSetting(
            POP_QUIZ_SETTINGS.lastShownDate,
            new Date().toISOString().slice(0, 10),
        );
    }, [setPopQuizSetting]);

    const recordQuizOffered = useCallback(() => {
        if (quizData) {
            void setPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId, String(quizData.quizId));
        }
    }, [quizData, setPopQuizSetting]);

    const recordDismissal = useCallback(() => {
        recordQuizOffered();
        recordShown();
    }, [recordQuizOffered, recordShown]);

    // If we have quiz items but fewer than the minimum required, bail out early and mark as shown.
    useEffect(() => {
        if (
            quizData &&
            !isItemSearchFetching &&
            supportedItems.length > 0 &&
            supportedItems.length < MIN_QUIZ_QUESTIONS &&
            !hasShownRef.current
        ) {
            hasShownRef.current = true;
            recordShown();
            setIsOpen(false);
        }
    }, [quizData, isItemSearchFetching, supportedItems, recordShown]);

    const onAccept = useCallback(async () => {
        if (!quizData || supportedItems.length === 0) {
            return;
        }

        // Start a server-side attempt record. Suppressed for debug quizzes in prod environment
        let newAttemptId: string | null = null;
        if (!isDebugQuizSuppressed(quizData)) {
            try {
                const result = await startAttempt.mutateAsync({quizId: String(quizData.quizId)});
                newAttemptId = result?.quizAttemptStart?.id ?? null;
                publishPopQuizEvent(
                    new QuizStarted(
                        {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                        {id: quizData.courseId},
                        Number(newAttemptId),
                    ),
                    quizData,
                );
            } catch {
                // Proceed without an attemptId — per-answer submissions will also be skipped
            }
        }

        void setPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId, String(quizData.quizId));
        setAttemptId(newAttemptId);
        setQuestions(supportedItems);
        setPhase('quiz');
    }, [quizData, supportedItems, startAttempt, setPopQuizSetting]);

    const onComplete = useCallback((finalCorrectCount: number) => {
        setCorrectCount(finalCorrectCount);
        setPhase('results');
    }, []);

    const onClose = useCallback(() => {
        setIsOpen(false);
        setAttemptId(null);
        setQuestions([]);
        setCorrectCount(0);
        setStopShowing(false);
        isSnoozingRef.current = false;
        setPhase('prompt');
        recordDismissal();
    }, [recordDismissal]);

    // Handles "Not now" / "Exit" from the prompt phase.
    // Snoozes for 60 days if the checkbox is checked; otherwise fires a dismissed event and closes.
    const handlePromptDismiss = useCallback(() => {
        if (stopShowing && quizData && !isSnoozingRef.current) {
            isSnoozingRef.current = true;
            void setPopQuizSetting(
                POP_QUIZ_SETTINGS.snoozedAt,
                new Date().toISOString().slice(0, 10),
            );
            publishPopQuizEvent(
                new PopQuizSnoozed(
                    {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                    {id: quizData.courseId},
                    LONG_SNOOZE_DAYS,
                ),
                quizData,
            );
            setPhase('snoozed');
        } else {
            if (quizData) {
                publishPopQuizEvent(
                    new PopQuizDismissed(
                        {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                        {id: quizData.courseId},
                    ),
                    quizData,
                );
            }
            onClose();
        }
    }, [stopShowing, quizData, supportedItems, setPopQuizSetting, onClose]);

    // Handles "Exit" from the quiz or results phase.
    // Snoozes for 60 days if the checkbox is checked; otherwise closes normally (3-day cap).
    const handleSnoozingExit = useCallback(() => {
        if (stopShowing && quizData && !isSnoozingRef.current) {
            isSnoozingRef.current = true;
            void setPopQuizSetting(
                POP_QUIZ_SETTINGS.snoozedAt,
                new Date().toISOString().slice(0, 10),
            );
            publishPopQuizEvent(
                new PopQuizSnoozed(
                    {id: quizData.quizId, numberOfQuestions: questions.length},
                    {id: quizData.courseId},
                    LONG_SNOOZE_DAYS,
                ),
                quizData,
            );
            setPhase('snoozed');
        } else {
            onClose();
        }
    }, [stopShowing, quizData, questions, setPopQuizSetting, onClose]);

    const onFeedback = useCallback(
        (rating: PopQuizFeedbackRating) => {
            if (quizData) {
                publishPopQuizEvent(
                    new PopQuizFeedbackSubmitted(
                        {id: quizData.quizId, numberOfQuestions: questions.length},
                        {id: quizData.courseId},
                        rating,
                    ),
                    quizData,
                );
            }
        },
        [quizData, questions.length],
    );

    const onRecommendedAssessmentSelected = useCallback(() => {
        if (quizData && recommendedAssessment) {
            publishPopQuizEvent(
                new PopQuizRecommendedAssessmentLinkSelected(
                    {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                    {id: quizData.courseId},
                    {
                        id: Number(recommendedAssessment.id),
                        title: recommendedAssessment.title,
                        isBeta: recommendedAssessment.isBeta,
                        domainId: selectPrimaryDomainId(recommendedAssessment),
                    },
                ),
                quizData,
            );
        }
    }, [quizData, recommendedAssessment, supportedItems]);

    const onContinueLearning = useCallback(() => {
        if (quizData && typeof window !== 'undefined') {
            publishPopQuizEvent(
                new PopQuizContinueCourseSelected(
                    {id: quizData.quizId, numberOfQuestions: questions.length},
                    {id: quizData.courseId},
                ),
                quizData,
            );
            recordShown();
            window.location.assign(quizData.courseUrl);
        }
    }, [quizData, questions.length, recordShown]);

    if (!enablePopQuizPP || !quizData) {
        return null;
    }

    return (
        <>
            {phase === 'prompt' && (
                <PopQuizPromptPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onAccept={onAccept}
                    onDismiss={handlePromptDismiss}
                    recommendedAssessment={recommendedAssessment}
                    onRecommendedAssessmentSelected={onRecommendedAssessmentSelected}
                />
            )}

            {phase === 'snoozed' && (
                <PopQuizSnoozedPhase
                    isOpen={isOpen}
                    onClose={() => {
                        recordDismissal();
                        setIsOpen(false);
                    }}
                />
            )}

            {phase === 'quiz' && (
                <PopQuizQuizPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    questions={questions}
                    attemptId={attemptId}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onComplete={onComplete}
                    onExit={handleSnoozingExit}
                    popQuizV2ExperienceEnabled={popQuizV2ExperienceEnabled}
                />
            )}

            {phase === 'results' && (
                <PopQuizResultsPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    correctCount={correctCount}
                    totalQuestions={questions.length}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onExit={handleSnoozingExit}
                    onContinueLearning={onContinueLearning}
                    onFeedback={onFeedback}
                />
            )}
        </>
    );
}
